
import { useRef, useEffect } from "react"
import { useLocation } from "react-router"

// should normalize dynamic urls and track page changes manually
export default function PageTracker({ ...props }) {

    const location = useLocation()

    const prevNormalizedLocation = useRef()

    useEffect(() => {
        // track normalized pages in matomo
        const normalized = normalizeLocation(location)

        const full = location.pathname + (location.search ?? '') + (location.hash ?? '')
        
        if (prevNormalizedLocation.current !== normalized) {
            prevNormalizedLocation.current = normalized

            if (window._paq) {
                // we only want to track a pageview if the normalized location changes, not when a query param changes
                // tracking logic here (_paq is matomo)

                window._paq.push(['setCustomUrl', normalized])
                window._paq.push(['setDocumentTitle', full])
                window._paq.push(['trackPageView'])

                // example for content tracking 
                // var content = document.getElementById('content') 
                //_paq.push(['trackContentImpressionsWithinNode', content]) 

                //enable measuring of outlinks and downloads 
                window._paq.push(['enableLinkTracking']) 
            }
        }
    }, [location])
}

// if these are followed in the path by a dynamic id number, we need to normalize those urls for our analytics tracker
// /pitch/:id/analysis/:id/test/:id
const dynamicIdsToNormalize = [
    'pitch',
    'club',
    'analysis',
    'test',
    'create-analysis',
]

function normalizeLocation(location) {
    // use regex to match and replace example:  /pitch/63  =>  /pitch/:id
    if (!location?.pathname) return undefined

    // we intentionally strip query params since we want to unify page traffic per page and unique alternate page version.
    // alternate page version being the same route with drastically different ui or function based on state, like 'performance' view
    // we handle these with the hash

    const path = location.pathname + location.hash

    const regexReplacements = dynamicIdsToNormalize.map(str => ({
        match: new RegExp(`(?:^|/)(${str})/\\d+(?=/|$|#)`, 'g'),
        replacement: `/$1/:id`,
    }))

    const normalized = regexReplacements.reduce((a, b) => a.replace(b.match, b.replacement), path)
    return normalized
}


// when doing manual page tracking (which normalization requires)
// lots of features will be needing manual handling as well:

// Resetting previously set custom dimensions
// Updating the referrer
// Video and Audio tracking
// Form tracking
// A/B testing
// Link tracking (outlinks and downloads)
// Content tracking
// https://developer.matomo.org/guides/spa-tracking

// for heatmaps: the trigger url is matched agains the actual url in browser, not any customUrl set by setCustomUrl
// https://developer.matomo.org/guides/heatmap-session-recording/reference#matchtrackerurl